import React from 'react';
import { ChevronLeft, ChevronRight, Download } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import './Proposals.css';
import ExportModal from '../../rfp/shared/components/ExportModal';
import PPTGenerationModal from '../../ppt/components/PPTGenerationModal';
import SuccessToast from '../../../shared/components/ui/SuccessToast';
import CustomLoader from '../../../shared/components/ui/CustomLoader';
import api from '../../../services/api';
import DashboardSkeleton from '../components/Skeletons/DashboardSkeleton';
import DeleteRFPModal from '../components/Modals/DeleteRFPModal';

// Sub-components
import MyProposalsHeader from '../components/MyProposalsHeader';
import MyProposalsStatCards from '../components/MyProposalsStatCards';
import MyProposalsFilterBar from '../components/MyProposalsFilterBar';
import ProposalCard from '../components/ProposalCard';
import AssignmentRowView from '../components/AssignmentRowView';

// Hooks & Helpers
import useProposalActions from '../hooks/useProposalActions';
import { getStatusValue, isDueSoon, ITEMS_PER_PAGE, isProposalExpired } from '../utils/proposalHelpers';
import { useScrollToTop } from '../../../utils/scrollUtils';

console.log("PROPOSALS PAGE LOADED (NEW VERSION V3 - RFP_IDS FIX)");

const MyProposals = ({ user, globalSearch }) => {
    const userDesignation = (user?.designation || '').toLowerCase();
    const isManager = userDesignation.includes('lead') || userDesignation.includes('manager');

    const navigate = useNavigate();
    const [showPPTModal, setShowPPTModal] = React.useState(false);

    // ── Core State ─────────────────────────────────────────────────────────
    const [rfpData, setRfpData] = React.useState([]);
    const [rfpStats, setRfpStats] = React.useState(null);
    const [metrics, setMetrics] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const [isFilterChanging, setIsFilterChanging] = React.useState(false);
    const [error, setError] = React.useState(null);
    const [searchQuery, setSearchQuery] = React.useState('');
    const [statusFilter, setStatusFilter] = React.useState('all');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = React.useState('');
    const [currentPage, setCurrentPage] = React.useState(1);
    const [selectedIds, setSelectedIds] = React.useState(() => {
        try { return JSON.parse(sessionStorage.getItem('selectedRfpIds') || '[]'); }
        catch { return []; }
    });
    const [showExport, setShowExport] = React.useState(false);
    const [selectedRfpId, setSelectedRfpId] = React.useState(null);
    const [activeMenuId, setActiveMenuId] = React.useState(null);
    const [itemToDelete, setItemToDelete] = React.useState(null);
    const [successToast, setSuccessToast] = React.useState(null);
    const [isFetchingFullData, setIsFetchingFullData] = React.useState(false);
    const [fetchingRfpTitle, setFetchingRfpTitle] = React.useState('');
    const [showMoreFilters, setShowMoreFilters] = React.useState(false);
    const menuRef = React.useRef(null);
    const moreFiltersRef = React.useRef(null);

    useScrollToTop([statusFilter, currentPage]);

    // ── Action Hook ────────────────────────────────────────────────────────
    const {
        isDeleting, isBulkDownloading,
        handleToggleSelect, handleActionClick, confirmDelete, handleBulkDownload
    } = useProposalActions({
        isManager, selectedIds, setSelectedIds, setRfpData, setError,
        setSuccessToast, setItemToDelete, setActiveMenuId,
        setIsFetchingFullData, setFetchingRfpTitle,
        setShowExport, setSelectedRfpId,
        fetchProposalsAndMetrics: () => fetchProposalsAndMetrics()
    });

    // ── Effects ────────────────────────────────────────────────────────────
    React.useEffect(() => {
        const handleClickOutside = (e) => {
            if (activeMenuId && menuRef.current && !menuRef.current.contains(e.target)) setActiveMenuId(null);
            if (showMoreFilters && moreFiltersRef.current && !moreFiltersRef.current.contains(e.target)) setShowMoreFilters(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [activeMenuId, showMoreFilters]);

    // Prevent background scrolling when overlay or modal is active
    React.useEffect(() => {
        const isAnyModalOpen = isFetchingFullData || isBulkDownloading || Boolean(itemToDelete) || showExport || showPPTModal;
        if (isAnyModalOpen) {
            const originalOverflow = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
            return () => {
                document.body.style.overflow = originalOverflow;
            };
        }
    }, [isFetchingFullData, isBulkDownloading, itemToDelete, showExport, showPPTModal]);

    React.useEffect(() => {
        if (selectedIds.length > 0 && typeof error === 'string' && error.startsWith('Please select')) setError(null);
    }, [selectedIds, error]);

    React.useEffect(() => { sessionStorage.setItem('selectedRfpIds', JSON.stringify(selectedIds)); }, [selectedIds]);

    React.useEffect(() => {
        const h = setTimeout(() => setDebouncedSearchQuery((searchQuery || globalSearch || '').trim()), 500);
        return () => clearTimeout(h);
    }, [searchQuery, globalSearch]);

    React.useEffect(() => { fetchProposalsAndMetrics(); }, [statusFilter, debouncedSearchQuery]); // eslint-disable-line

    // ── API ────────────────────────────────────────────────────────────────
    const fetchProposalsAndMetrics = async () => {
        const isSubsequentFetch = !loading;
        if (isSubsequentFetch) {
            setIsFilterChanging(true);
        }
        try {
            const params = {};
            const apiStatus = (statusFilter === 'all' || statusFilter === 'total') ? '' : statusFilter;
            const apiSearch = (debouncedSearchQuery || globalSearch || '').trim();
            if (apiStatus) params.status = apiStatus;
            if (apiSearch) params.search = apiSearch;

            if (isManager) {
                const res = await api.get('/rfp/user/all', { params });
                const d = res.data;
                setRfpData((d && typeof d === 'object' && !Array.isArray(d))
                    ? (d.rfps || d.users || d.proposals || d.results || [])
                    : (Array.isArray(d) ? d : []));
            } else {
                // Fetch employee assignments — new grouped schema:
                // { total_rfps, groups: [{ rfp_id, proposal_title, client_name,
                //   global_status, total_sections, completed_sections, latest_assigned_at, sections }] }
                const res = await api.get('/rfp/employee/my-assignments', { params });
                const groups = res.data?.groups || [];
                setRfpData(groups.map(g => ({
                    ...g, id: g.rfp_id, rfp_id: g.rfp_id,
                    completed_sections: g.completed_sections ?? 0,
                    total_sections: g.total_sections ?? (g.sections?.length ?? 0),
                    created_at: g.latest_assigned_at,
                    sections: g.sections || [],
                    isAssignment: true
                })));
            }

            try { const r = await api.get('/dashboard/proposal-metrics'); if (r.status === 200) setMetrics(r.data); } catch { /* silent */ }
        } catch (err) {
            console.error('Data fetch error:', err);
            setError('Unable to reach the server.');
        } finally {
            setLoading(false);
            if (isSubsequentFetch) {
                setTimeout(() => {
                    setIsFilterChanging(false);
                }, 300);
            }
        }
    };

    // ── Filtering & Pagination ─────────────────────────────────────────────
    const filteredResults = (Array.isArray(rfpData) ? rfpData : [])
        .filter(rfp => {
            if (statusFilter === 'all') return true;
            if (statusFilter === 'expired') return isProposalExpired(rfp);
            if (rfpStats?.[statusFilter]?.rfp_ids)
                return rfpStats[statusFilter].rfp_ids.includes(String(rfp._id || rfp.rfp_id || rfp.id));
            return true;
        })
        .sort((a, b) => {
            if (!isManager) {
                const codeA = Number(a.global_status?.code || a.status_code || a.status || a.statusCode);
                const codeB = Number(b.global_status?.code || b.status_code || b.status || b.statusCode);
                const isClosedA = codeA === 56 || codeA === 57;
                const isClosedB = codeB === 56 || codeB === 57;
                if (isClosedA !== isClosedB) {
                    return isClosedA ? 1 : -1;
                }
            }
            return new Date(b.created_at || b.uploaded_at || 0) - new Date(a.created_at || a.uploaded_at || 0);
        });

    const totalPages = Math.ceil(filteredResults.length / ITEMS_PER_PAGE);
    const paginatedData = filteredResults.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
    const handlePageChange = (p) => { if (p >= 1 && p <= totalPages) setCurrentPage(p); };

    // ── Computed Stats ─────────────────────────────────────────────────────
    const displayTotalProposals = metrics?.total_active ?? rfpData.length;
    const displayDueThisWeek = metrics?.due_this_week ?? rfpData.filter(r => isDueSoon(r.proposal_deadline || r.extraction_data?.submission_deadline)).length;
    const submittedCount = metrics?.submitted ?? rfpData.filter(r => getStatusValue(r).toLowerCase().includes('submit')).length;
    const inProgressCount = metrics?.in_progress ?? rfpData.filter(r => { const s = (getStatusValue(r) || '').toLowerCase(); return s.includes('progress') || s.includes('review') || s === 'draft'; }).length;

    if (loading) return <DashboardSkeleton isManager={isManager} />;

    return (
        <>
            <div className="proposals-page">
                <MyProposalsHeader
                    isManager={isManager}
                    selectedIds={selectedIds}
                    isBulkDownloading={isBulkDownloading}
                    handleBulkDownload={handleBulkDownload}
                    onBulkDelete={() => setItemToDelete({ proposal_title: `${selectedIds.length} selected proposals`, filename: 'selected items', multiple: true })}
                />

                {error && <div className="login-error-message" style={{ margin: '0 0 2rem 0' }}>{error}</div>}

                <MyProposalsStatCards
                    displayTotalProposals={displayTotalProposals}
                    inProgressCount={inProgressCount}
                    displayDueThisWeek={displayDueThisWeek}
                    submittedCount={submittedCount}
                    isManager={isManager}
                    metrics={metrics}
                />

                <div className={`table-container-main ${!isManager ? 'member-assignments-container' : ''}`}>
                    <MyProposalsFilterBar
                        searchQuery={searchQuery} setSearchQuery={setSearchQuery}
                        setCurrentPage={setCurrentPage} statusFilter={statusFilter}
                        setStatusFilter={setStatusFilter} rfpStats={rfpStats}
                        showMoreFilters={showMoreFilters} setShowMoreFilters={setShowMoreFilters}
                        moreFiltersRef={moreFiltersRef}
                        selectedCount={selectedIds.length}
                        onDownloadBulk={handleBulkDownload}
                        onDeleteBulk={() => setItemToDelete({
                            proposal_title: `${selectedIds.length} selected proposals`,
                            filename: 'selected items',
                            multiple: true
                        })}
                        isBulkDownloading={isBulkDownloading}
                        isManager={isManager}
                    />

                    {isManager ? (
                        <div className="proposals-grid">
                            {isFilterChanging ? (
                                <div className="filter-loader-container">
                                    <CustomLoader className="loader-spin" size={40} />
                                    <p>Updating proposals...</p>
                                </div>
                            ) : (
                                <>
                                    {paginatedData.map((prop, idx) => {
                                        const rfpId = prop._id || prop.rfp_id || prop.id;
                                        const isSelected = rfpId != null ? selectedIds.some(i => String(i) === String(rfpId)) : false;
                                        return <ProposalCard key={rfpId || idx} prop={prop} isSelected={isSelected} isManager={isManager} onToggleSelect={handleToggleSelect} onActionClick={handleActionClick} />;
                                    })}
                                    {paginatedData.length === 0 && <div className="proposals-empty">No proposals found.</div>}
                                </>
                            )}
                        </div>
                    ) : (
                        <div className="assignments-table-view">
                            {isFilterChanging ? (
                                <div className="filter-loader-container">
                                    <CustomLoader className="loader-spin" size={40} />
                                    <p>Updating assignments...</p>
                                </div>
                            ) : (
                                <>
                                    <div className="assignments-header-row">
                                        <div className="header-col col-main">Proposal &amp; Client</div>
                                        <div className="header-col col-sections">Progress</div>
                                        <div className="header-col col-assigned">Latest Assigned At</div>
                                        <div className="header-col col-actions">Actions</div>
                                    </div>
                                    <div className="assignments-list">
                                         {(() => {
                                             const activeAssignments = paginatedData.filter(prop => {
                                                 const code = Number(prop.global_status?.code || prop.status_code || prop.status || prop.statusCode);
                                                 return code !== 56 && code !== 57;
                                             });
                                             const closedAssignments = paginatedData.filter(prop => {
                                                 const code = Number(prop.global_status?.code || prop.status_code || prop.status || prop.statusCode);
                                                 return code === 56 || code === 57;
                                             });

                                             return (
                                                 <>
                                                     {activeAssignments.map((prop, idx) => (
                                                         <AssignmentRowView key={prop._id || prop.rfp_id || prop.id || idx} prop={prop} />
                                                     ))}
                                                     
                                                     {closedAssignments.length > 0 && (
                                                         <>
                                                             <div 
                                                                 className="closed-assignments-divider"
                                                                 style={{
                                                                     padding: '16px 16px 12px 16px',
                                                                     fontSize: '11px',
                                                                     fontWeight: '700',
                                                                     color: '#94A3B8',
                                                                     letterSpacing: '0.05em',
                                                                     textTransform: 'uppercase',
                                                                     borderTop: '1px solid #E2E8F0',
                                                                     marginTop: activeAssignments.length > 0 ? '16px' : '0',
                                                                     display: 'flex',
                                                                     alignItems: 'center',
                                                                     gap: '8px'
                                                                 }}
                                                             >
                                                                 <span>Recently Closed Assignments</span>
                                                             </div>
                                                             {closedAssignments.map((prop, idx) => (
                                                                 <AssignmentRowView key={prop._id || prop.rfp_id || prop.id || idx} prop={prop} />
                                                             ))}
                                                         </>
                                                     )}
                                                     
                                                     {paginatedData.length === 0 && <div className="proposals-empty">No assignments found.</div>}
                                                 </>
                                             );
                                         })()}
                                     </div>
                                </>
                            )}
                        </div>
                    )}

                    <div className="table-footer">
                        <span className="footer-info">
                            Displaying {Math.min(filteredResults.length, (currentPage - 1) * ITEMS_PER_PAGE + 1)} to {Math.min(filteredResults.length, currentPage * ITEMS_PER_PAGE)} out of {filteredResults.length} proposals
                        </span>
                        {totalPages > 1 && (
                            <div className="pagination">
                                <button className="page-nav" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}><ChevronLeft size={18} /></button>
                                {[...Array(totalPages)].map((_, i) => (
                                    <button key={i} className={`page-num ${currentPage === i + 1 ? 'active' : ''}`} onClick={() => handlePageChange(i + 1)}>{i + 1}</button>
                                ))}
                                <button className="page-nav" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}><ChevronRight size={18} /></button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {(() => {
                const selectedRfpData = rfpData.find(item => String(item._id || item.rfp_id || item.id) === String(selectedRfpId));
                return (
                    <>
                        {showExport && (
                            <ExportModal
                                isOpen={showExport}
                                onClose={() => setShowExport(false)}
                                rfpId={selectedRfpId}
                                analysisData={selectedRfpData}
                                onPPTClick={() => {
                                    setShowExport(false);
                                    setShowPPTModal(true);
                                }}
                            />
                        )}
                        <PPTGenerationModal
                            isOpen={showPPTModal}
                            onClose={() => setShowPPTModal(false)}
                            rfpId={selectedRfpId}
                            navigate={navigate}
                            editableData={selectedRfpData}
                        />
                    </>
                );
            })()}

            {itemToDelete && (
                <DeleteRFPModal
                    item={itemToDelete}
                    isDeleting={isDeleting}
                    onConfirm={() => confirmDelete(itemToDelete)}
                    onCancel={() => setItemToDelete(null)}
                />
            )}

            <SuccessToast toast={successToast} />

            {isFetchingFullData && (
                <div
                    className="fetching-overlay"
                    onWheel={(e) => e.stopPropagation()}
                    onTouchMove={(e) => e.stopPropagation()}
                >
                    <div className="fetching-content">
                        <CustomLoader className="loader-spin" size={48} />
                        <div className="fetching-text">
                            <h3>Loading Analysis Result</h3>
                            <p>Fetching full details for <strong>{fetchingRfpTitle}</strong></p>
                        </div>
                    </div>
                </div>
            )}

            {isBulkDownloading && (
                <div
                    className="fetching-overlay"
                    style={{ zIndex: 10000 }}
                    onWheel={(e) => e.stopPropagation()}
                    onTouchMove={(e) => e.stopPropagation()}
                >
                    <div className="fetching-content" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem', padding: '2.5rem', borderRadius: '12px', minWidth: '320px', background: 'white', boxShadow: '0 10px 25px rgba(0,0,0,0.1)' }}>
                        <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                            <CustomLoader className="loader-spin" size={60} />
                            <div style={{ position: 'absolute', color: '#FF5526', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Download size={22} />
                            </div>
                        </div>
                        <div className="fetching-text" style={{ textAlign: 'center', margin: 0 }}>
                            <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#1E293B', marginBottom: '4px' }}>Downloading PDF</h3>
                            <p style={{ fontSize: '13px', color: '#64748B', margin: 0 }}>Please wait while we bundle your proposals...</p>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default MyProposals;
